import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type {
  SidePanelScrollContainerProps,
  SidePanelScrollContextValue,
  SidePanelScrollProviderProps,
} from './SidePanelScrollContext.types';

const SidePanelScrollContext = createContext<SidePanelScrollContextValue | null>(null);

// Stable no-op fallback for consumers rendered outside a SidePanelScrollProvider
// (e.g. a widget shown in the Library preview modal). MUST be a module-level
// singleton: returning a fresh object here would hand out new function
// identities every render, and a consumer that feeds `scrollToTop` into a
// useCallback/useEffect dependency would re-fire forever (max update depth).
const NOOP_SCROLL: SidePanelScrollContextValue = {
  scrollToTop: () => {},
  getScrollPosition: () => 0,
  setScrollPosition: () => {},
  registerScrollContainer: () => {},
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its provider
export function useSidePanelScroll() {
  return useContext(SidePanelScrollContext) ?? NOOP_SCROLL;
}

/**
 * Provider that manages scroll state for side panels.
 * Does NOT render a scroll container - use SidePanelScrollContainer for that.
 */
export function SidePanelScrollProvider({ children }: SidePanelScrollProviderProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const registerScrollContainer = useCallback((element: HTMLDivElement | null) => {
    scrollContainerRef.current = element;
  }, []);

  const scrollToTop = useCallback(() => {
    // Use requestAnimationFrame to ensure DOM has updated before scrolling
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    });
  }, []);

  const getScrollPosition = useCallback(() => {
    return scrollContainerRef.current?.scrollTop ?? 0;
  }, []);

  const setScrollPosition = useCallback((position: number) => {
    // Use requestAnimationFrame to ensure DOM has updated before scrolling
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = position;
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      scrollToTop,
      getScrollPosition,
      setScrollPosition,
      registerScrollContainer,
    }),
    [scrollToTop, getScrollPosition, setScrollPosition, registerScrollContainer],
  );

  return (
    <SidePanelScrollContext.Provider value={value}>{children}</SidePanelScrollContext.Provider>
  );
}

/**
 * The actual scroll container. Registers itself with the context.
 */
export function SidePanelScrollContainer({ children, scrollKey }: SidePanelScrollContainerProps) {
  const { registerScrollContainer, scrollToTop } = useSidePanelScroll();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, scrollKey]);

  return (
    <div ref={registerScrollContainer} className="flex-1 overflow-y-auto">
      {children}
    </div>
  );
}
